// @givmo/board — record a governance figure.
//
// The citation field is REQUIRED here because it is required at the database and on the command —
// there is deliberately no path by which a number arrives un-attributed. The form enforces it in the
// same direction rather than treating it as optional metadata the officer might add later.
//
// The shape rules mirror the backend's CHECK constraints, so the form cannot submit a row the database
// will refuse:
//   * notice period → per meeting type, measured in DAYS
//   * quorum / voting threshold → board-wide, measured either as a SEAT COUNT or as a FRACTION
//   * exactly the value fields that basis uses are sent; the rest are omitted
//
// A FRACTION is captured as two whole numbers and echoed back by the backend as the organization wrote it
// ("2 of 3"), never collapsed into a percentage. Echoing something the organization did not type is how a
// recorded value quietly becomes a derived one.
//
// CBG-RC11 (D-CBG-26; frames g2, g3): two more rules, how the board approves its minutes and a closed
// session's own minutes, recorded as a WAY (`basis: 'choice'`, `choice_value`) rather than a number.
// Each way is a radio tile carrying its explanation at the moment it is chosen (the givmo-design
// skill: a fork-deciding choice is disclosed when it is made), then the same required citation. The
// backend admits each rule's own ways only, and a way never beside a number.
//
// A concurrent second recording of the same figure resolves to a 409 rather than a silent overwrite —
// two different numbers for one parameter, every notice figure computed against whichever is in force.
// The dialog surfaces that refusal as itself and keeps the officer's input, so they can re-read what
// is now in force and decide again.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardGovernanceParameterBasis,
  type BoardGovernanceParameterKind,
  type BoardMeetingType,
} from '@givmo/api-client';
import {
  Button,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Select,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { minutesRuleWords as rc11, type RuleWay } from '../minutes/minutes-rule-words';
import {
  APPROVAL_RULE_DEFAULT,
  APPROVAL_RULE_WAYS,
  isApprovalRuleKind,
  type ApprovalRuleKind,
} from './parameters-decode';

const KIND_OPTIONS: readonly { value: BoardGovernanceParameterKind; label: string }[] = [
  { value: 'notice_period', label: 'Notice period' },
  { value: 'quorum', label: 'Quorum' },
  { value: 'voting_threshold', label: 'Voting threshold' },
  { value: 'minutes_approval', label: rc11.minutesRuleTitle },
  { value: 'closed_session_minutes_approval', label: rc11.closedRuleTitle },
];

const MEETING_TYPE_OPTIONS: readonly { value: BoardMeetingType; label: string }[] = [
  { value: 'regular', label: 'Regular meetings' },
  { value: 'annual', label: 'Annual meetings' },
  { value: 'special', label: 'Special meetings' },
];

const BASIS_OPTIONS: readonly {
  value: Exclude<BoardGovernanceParameterBasis, 'days'>;
  label: string;
}[] = [
  { value: 'seat_count', label: 'A number of seats' },
  { value: 'fraction', label: 'A fraction of seats' },
];

export interface RecordParameterDialogProps {
  orgRef: string;
  /**
   * CBG-RC11: the way each approval rule says now (recorded, or Givmo's starting point), which the
   * dialog starts on when that rule is picked: recording another is a deliberate choice.
   */
  inForce?: Partial<Record<ApprovalRuleKind, string>>;
  onClose: () => void;
  onRecorded: () => void;
}

export function RecordParameterDialog({
  orgRef,
  inForce = {},
  onClose,
  onRecorded,
}: RecordParameterDialogProps) {
  const operations = useBoardOperations();
  const [kind, setKind] = useState<BoardGovernanceParameterKind>('notice_period');
  // CBG-RC11: the way chosen for an approval rule, per rule, starting on the one in force.
  const [ways, setWays] = useState<Record<ApprovalRuleKind, string>>(() => ({
    minutes_approval: inForce.minutes_approval ?? APPROVAL_RULE_DEFAULT.minutes_approval,
    closed_session_minutes_approval:
      inForce.closed_session_minutes_approval ??
      APPROVAL_RULE_DEFAULT.closed_session_minutes_approval,
  }));
  const [meetingType, setMeetingType] = useState<BoardMeetingType>('regular');
  const [basis, setBasis] = useState<Exclude<BoardGovernanceParameterBasis, 'days'>>('seat_count');
  const [days, setDays] = useState('');
  const [count, setCount] = useState('');
  const [numerator, setNumerator] = useState('');
  const [denominator, setDenominator] = useState('');
  const [citation, setCitation] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isNoticePeriod = kind === 'notice_period';
  const ruleKind = isApprovalRuleKind(kind) ? kind : null;
  const effectiveBasis: BoardGovernanceParameterBasis = isNoticePeriod ? 'days' : basis;

  const record = useMutation({
    mutationFn: async () => {
      if (ruleKind !== null) {
        // A way, never a number: the backend refuses a number field beside a choice.
        await operations.officerRecordBoardGovernanceParameter({
          path: { org_ref: orgRef },
          body: {
            parameter_kind: ruleKind,
            basis: 'choice',
            choice_value: ways[ruleKind] as RuleWay,
            bylaw_citation: citation.trim(),
            ...(note.trim().length > 0 ? { note: note.trim() } : {}),
          },
          throwOnError: true,
        });
        return;
      }
      // Send exactly the value fields this basis uses — the backend's CHECK pins the pairing, so a
      // stray field is a refusal rather than an ignored key.
      const values =
        effectiveBasis === 'days'
          ? { days_value: toWholeNumber(days) }
          : effectiveBasis === 'seat_count'
            ? { count_value: toWholeNumber(count) }
            : {
                numerator: toWholeNumber(numerator),
                denominator: toWholeNumber(denominator),
              };

      await operations.officerRecordBoardGovernanceParameter({
        path: { org_ref: orgRef },
        body: {
          parameter_kind: kind,
          basis: effectiveBasis,
          bylaw_citation: citation.trim(),
          ...(isNoticePeriod ? { meeting_type: meetingType } : {}),
          ...(note.trim().length > 0 ? { note: note.trim() } : {}),
          ...values,
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Rule recorded.');
      onRecorded();
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that rule. Please try again.',
      );
    },
  });

  const valueReady =
    ruleKind !== null
      ? ways[ruleKind].length > 0
      : effectiveBasis === 'days'
        ? isWholeNumber(days)
        : effectiveBasis === 'seat_count'
          ? isWholeNumber(count)
          : isWholeNumber(numerator) && isWholeNumber(denominator);

  const canSave = valueReady && citation.trim().length > 0 && !record.isPending;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !record.isPending && onClose()}>
      <Dialog.Content
        aria-label="Add a rule"
        className="flex flex-col gap-16"
        testId="parameters-record-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Add a rule
        </Dialog.Title>

        <Select
          label="Which rule"
          appearance="saas"
          value={kind}
          onValueChange={(next) => next !== null && setKind(next as BoardGovernanceParameterKind)}
          options={KIND_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
          testId="parameters-kind"
        />

        {ruleKind !== null ? (
          // CBG-RC11: each way with its explanation, at the moment of choosing (g2, g3).
          <RadioTileGroup
            value={ways[ruleKind]}
            onValueChange={(next) => setWays((current) => ({ ...current, [ruleKind]: next }))}
            aria-label={
              ruleKind === 'minutes_approval' ? rc11.minutesRuleTitle : rc11.closedRuleTitle
            }
            className="flex flex-col gap-12"
            testId="parameters-way"
          >
            {APPROVAL_RULE_WAYS[ruleKind].map((way) => (
              <RadioTile
                key={way}
                value={way}
                label={rc11.wayLabel(way)}
                description={rc11.wayDescription(way)}
                testId={`parameters-way-${way}`}
              />
            ))}
          </RadioTileGroup>
        ) : isNoticePeriod ? (
          <>
            <Select
              label="Applies to"
              appearance="saas"
              value={meetingType}
              onValueChange={(next) => next !== null && setMeetingType(next as BoardMeetingType)}
              options={MEETING_TYPE_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              testId="parameters-meeting-type"
            />
            <TextField
              label="Days of notice"
              inputMode="numeric"
              value={days}
              onChange={(event) => setDays(event.target.value)}
              testId="parameters-days"
            />
            <FieldNote>Whole days, as your governing documents state them.</FieldNote>
          </>
        ) : (
          <>
            <Select
              label="Stated as"
              appearance="saas"
              value={basis}
              onValueChange={(next) =>
                next !== null && setBasis(next as Exclude<BoardGovernanceParameterBasis, 'days'>)
              }
              options={BASIS_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              testId="parameters-basis"
            />
            {basis === 'seat_count' ? (
              <TextField
                label="Number of seats"
                inputMode="numeric"
                value={count}
                onChange={(event) => setCount(event.target.value)}
                testId="parameters-count"
              />
            ) : (
              <div className="grid grid-cols-2 gap-12">
                <TextField
                  label="This many"
                  inputMode="numeric"
                  value={numerator}
                  onChange={(event) => setNumerator(event.target.value)}
                  testId="parameters-numerator"
                />
                <TextField
                  label="Out of"
                  inputMode="numeric"
                  value={denominator}
                  onChange={(event) => setDenominator(event.target.value)}
                  testId="parameters-denominator"
                />
              </div>
            )}
          </>
        )}

        <TextField
          label="Where this comes from"
          value={citation}
          onChange={(event) => setCitation(event.target.value)}
          testId="parameters-citation"
        />
        <FieldNote>
          {ruleKind === 'closed_session_minutes_approval'
            ? rc11.closedRuleCitationNote
            : 'The section of your own governing documents, e.g. “Art. III §4”. Required — a rule without it isn’t attributable to anything.'}
        </FieldNote>

        <Textarea
          label="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          testId="parameters-note"
        />

        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
          Recording a new rule replaces the one in force and keeps the old one in your history —
          nothing already recorded changes.
        </p>

        {error !== null ? (
          <ErrorMessage testId="parameters-record-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={record.isPending}
            onClick={onClose}
            testId="parameters-record-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={record.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              record.mutate();
            }}
            testId="parameters-record-save"
          >
            Record it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** Whether the field holds a plain whole number (the only shape these figures take). */
function isWholeNumber(value: string): boolean {
  return /^\d+$/.test(value.trim());
}

function toWholeNumber(value: string): number {
  return Number.parseInt(value.trim(), 10);
}
